import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import PhoneAuthSlide from './components/PhoneAuthSlide';
import MeasurementsSlide from './components/MeasurementsSlide';
import MannequinAdjustSlide from './components/MannequinAdjustSlide';
import SelfieSlide from './components/SelfieSlide';
import ResultsSlideNew from './components/ResultsSlideNew';
import CacheChoiceModal from './components/CacheChoiceModal';
import LanguageSwitcher from './components/LanguageSwitcher';
import { clearUserCache, getUserDataFromCache, hasCachedData, getAnonymousId } from './services/cacheService';
import { getShopperDetails, convertImageUrlToBase64 as convertAvatarToBase64, createShopper } from './services/shopperService';
import { onAuthStateChanged, signOut } from './services/firebaseAuth';
import { fetchSizeMappings } from './config/dataMapping';
import { computeProfile } from './services/sizeRecommendationService';
import { trackPageView, trackIframeOpen } from './services/analyticsService';
import { getCurrentSubscription } from './services/subscriptionService';
import SubscriptionErrorScreen from './components/SubscriptionErrorScreen';
import logger from './services/logger';
import logo from './assets/Mirror-black.png';

function App() {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [showCacheModal, setShowCacheModal] = useState(false);
  // null = pas encore chargé, object = chargé, 'error' = échec réseau
  const [subscription, setSubscription] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'
  const [cachedUserData, setCachedUserData] = useState(null);
  const [isFromAccount, setIsFromAccount] = useState(false);
  const [connectedUser, setConnectedUser] = useState(null);
  const [userData, setUserData] = useState({
    gender: '',
    height: '',
    weight: '',
    age: null,
    deltaChest: 0,
    deltaWaist: 0,
    deltaHips: 0,
    manualChest: null,
    manualWaist: null,
    manualHips: null,
    computedMeasurements: null,
    selfieBase64: '',
    clothingItem: null,
    avatarUrl: '',
    avatarBase64: '',
    customerDomain: '',
    productId: '',
    shopperId: '',
  });

  // Refs pour éviter le stale closure dans les handlers analytics
  const analyticsRef = useRef({ customerDomain: '', productId: '', firebaseUid: null });
  // Empêche de renvoyer iframe_opened à chaque réouverture (l'iframe reste montée entre ouvertures/fermetures)
  const hasTrackedOpenRef = useRef(false);

  // Détection du vêtement depuis la page parente
  useEffect(() => {
    const handleMessage = async (event) => {
      try {
        if (event.data.type === 'CLOTHING_ITEM_DATA') {
          const clothingItem = event.data.item;
          const domain = clothingItem?.customerDomain || '';
          const productId = clothingItem?.id || '';

          analyticsRef.current = { ...analyticsRef.current, customerDomain: domain, productId };

          setUserData((prev) => ({
            ...prev,
            clothingItem,
            customerDomain: domain,
            productId,
          }));

          // Tracker la vue de page produit (impression du widget)
          // UUID anonyme : généré maintenant si nouveau visiteur, sinon récupéré depuis localStorage
          trackPageView(domain, productId, getAnonymousId());

          // Récupérer l'état de l'abonnement dès que le domaine est connu
          if (domain) {
            setSubscriptionStatus('loading');
            try {
              const sub = await getCurrentSubscription(domain);
              setSubscription(sub);
              setSubscriptionStatus('loaded');
            } catch (subError) {
              logger.error('❌ Erreur récupération abonnement:', subError);
              // En cas d'erreur réseau on laisse passer pour ne pas bloquer l'utilisateur
              setSubscriptionStatus('error');
            }
          }
        }

        // Message pour ouvrir la popup
        if (event.data.type === 'OPEN_MIRROR') {
          setIsOpen(true);
          // Ne tracker que la première ouverture depuis l'atterrissage sur la page (même granularité que page_view)
          if (!hasTrackedOpenRef.current) {
            hasTrackedOpenRef.current = true;
            const { customerDomain, productId, firebaseUid } = analyticsRef.current;
            // Firebase UID si connecté, sinon UUID anonyme
            const customerId = firebaseUid || getAnonymousId();
            trackIframeOpen(customerDomain, productId, customerId);
          }
        }

        // Message pour fermer la popup
        if (event.data.type === 'CLOSE_MIRROR') {
          setIsOpen(false);
        }
      } catch (error) {
        logger.error('Erreur lors du traitement du message:', error);
      }
    };

    // Demander les infos du vêtement à la page parente
    window.parent.postMessage({ type: 'REQUEST_CLOTHING_ITEM' }, '*');

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Synchroniser le Firebase UID dans la ref pour les handlers analytics
  useEffect(() => {
    analyticsRef.current.firebaseUid = connectedUser?.uid || null;
  }, [connectedUser]);

  // Initialisation au démarrage
  useEffect(() => {
    setIsOpen(true);
    // Pré-charger les mappings de tailles depuis l'API
    fetchSizeMappings();
  }, []);

  // Fonction pour vérifier et afficher le modal si un compte est connecté avec des données
  const checkAndShowAccountModal = async (user) => {
    if (!user) return false;

    try {
      // Récupérer les données du shopper depuis le backend
      const shopperData = await getShopperDetails(user, true);

      if (shopperData && shopperData.gender && shopperData.height && shopperData.weight) {
        // Stocker l'ID BDD du shopper pour les endpoints gateway
        if (shopperData.id) {
          setUserData((prev) => ({ ...prev, shopperId: String(shopperData.id) }));
        }

        // Préparer les données pour le modal
        const accountData = {
          gender: shopperData.gender,
          height: shopperData.height,
          weight: shopperData.weight,
          sizeTop: shopperData.sizeTop || '',
          sizeBottom: shopperData.sizeBottom || '',
          avatarUrl: shopperData.avatar_path || '',
        };

        setCachedUserData(accountData);
        setIsFromAccount(true);
        if (!isSubscriptionBlocking) setShowCacheModal(true);
        logger.log('✅ Données du compte récupérées, affichage du modal');
        return true;
      } else {
        logger.log('ℹ️ Compte connecté mais données incomplètes');
        return false;
      }
    } catch (error) {
      logger.warn('Impossible de récupérer les données du compte:', error);
      return false;
    }
  };

  // Détecter si un utilisateur est déjà connecté ou si des données sont en cache
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (user) => {
      if (user) {
        // Utilisateur connecté → afficher le modal avec les données du compte
        logger.log('🔐 Utilisateur déjà connecté détecté:', user.uid);
        setConnectedUser(user);
        await checkAndShowAccountModal(user);
      } else {
        setConnectedUser(null);

        // Pas de compte connecté → vérifier le cache local
        if (hasCachedData()) {
          const cached = getUserDataFromCache();
          // Vérifier que les données sont valides (au moins gender, height, weight)
          if (cached && cached.gender && cached.height && cached.weight) {
            logger.log('📦 Données en cache détectées, affichage du modal');
            setCachedUserData(cached);
            setIsFromAccount(false);
            if (!isSubscriptionBlocking) setShowCacheModal(true);
          }
        }
      }
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gérer la connexion - utilisateur existant
  const handlePhoneAuthComplete = async ({ user: authUser }) => {
    setIsAuthenticated(true);
    setConnectedUser(authUser); // Stocker l'utilisateur connecté pour le "Recommencer"

    try {
      // Récupérer les données du shopper depuis PostgreSQL (avec mapping automatique)
      let avatarUrl = '';
      let avatarBase64 = '';
      let shopperData = null;

      try {
        // Passer explicitement l'utilisateur Firebase pour éviter les problèmes de timing
        // mapData=true par défaut pour obtenir les données au format application
        shopperData = await getShopperDetails(authUser, true);

        if (shopperData) {
          logger.log('✅ Shopper récupéré avec données mappées:', {
            gender: shopperData.gender,
            height: shopperData.height,
            weight: shopperData.weight,
            sizeTop: shopperData.sizeTop,
            sizeBottom: shopperData.sizeBottom,
            hasAvatar: !!shopperData.avatar_path,
          });

          // Récupérer l'avatar si disponible
          if (shopperData.avatar_path) {
            avatarUrl = shopperData.avatar_path;
            logger.log('Avatar récupéré depuis mirror-api:', avatarUrl);

            // Convertir l'image en base64 pour l'API VTO
            try {
              avatarBase64 = await convertAvatarToBase64(avatarUrl);
              logger.log('Avatar converti en base64');
            } catch (conversionError) {
              logger.warn('Impossible de convertir l\'avatar en base64:', conversionError);
            }
          }
        }
      } catch (apiError) {
        logger.warn('Impossible de récupérer le shopper depuis mirror-api:', apiError);
      }

      // Si un shopper existe avec avatar et données complètes, aller directement aux résultats
      if (avatarUrl && shopperData) {
        // Utilisateur existant avec avatar et données - aller directement aux résultats
        setUserData((prev) => ({
          ...prev,
          // Données d'avatar
          avatarUrl: avatarUrl,
          avatarBase64: avatarBase64,
          // ID BDD du shopper pour la gateway
          shopperId: shopperData.id ? String(shopperData.id) : prev.shopperId,
          // Données mappées du shopper (pour la recommandation de taille)
          gender: shopperData.gender || prev.gender,
          height: shopperData.height || prev.height,
          weight: shopperData.weight || prev.weight,
          sizeTop: shopperData.sizeTop || prev.sizeTop,
          sizeBottom: shopperData.sizeBottom || prev.sizeBottom,
        }));
        setCurrentSlide(3);
      } else if (shopperData && !avatarUrl) {
        // Shopper existe mais sans avatar - pré-remplir les données et continuer le flux
        setUserData((prev) => ({
          ...prev,
          // ID BDD du shopper pour la gateway
          shopperId: shopperData.id ? String(shopperData.id) : prev.shopperId,
          gender: shopperData.gender || prev.gender,
          height: shopperData.height || prev.height,
          weight: shopperData.weight || prev.weight,
          sizeTop: shopperData.sizeTop || prev.sizeTop,
          sizeBottom: shopperData.sizeBottom || prev.sizeBottom,
        }));
        setCurrentSlide(0);
      } else {
        // Nouveau shopper ou erreur - continuer le flux normal
        setCurrentSlide(0);
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du profil:', error);
      // En cas d'erreur, continuer le flux normal
      setCurrentSlide(0);
    }
  };

  // Gérer le "skip" - continuer sans compte (retour à la slide des mesures)
  const handleSkipAuth = () => {
    setIsAuthenticated(false);

    // Vérifier si des données sont en cache local
    if (hasCachedData()) {
      const cached = getUserDataFromCache();
      // Vérifier que les données sont valides (au moins gender, height, weight)
      if (cached && cached.gender && cached.height && cached.weight) {
        setCachedUserData(cached);
        setIsFromAccount(false); // C'est du cache local, pas un compte
        if (!isSubscriptionBlocking) setShowCacheModal(true);
        return;
      }
    }

    // Pas de cache valide, retour aux mesures
    setCurrentSlide(0);
  };

  // Utiliser les données (du compte ou du cache)
  const handleUseCachedData = async () => {
    if (cachedUserData) {
      // Calculer le profil physique depuis les données en cache avant de naviguer
      let measurements = null;
      try {
        measurements = await computeProfile({
          gender:     cachedUserData.gender,
          height:     cachedUserData.height,
          weight:     cachedUserData.weight,
          age:        cachedUserData.age,
          deltaChest: cachedUserData.deltaChest ?? 0,
          deltaWaist: cachedUserData.deltaWaist ?? 0,
          deltaHips:  cachedUserData.deltaHips  ?? 0,
          manualChest: cachedUserData.manualChest ?? null,
          manualWaist: cachedUserData.manualWaist ?? null,
          manualHips:  cachedUserData.manualHips  ?? null,
        });
        logger.log('✅ Profil calculé depuis le cache');
      } catch (err) {
        logger.error('Erreur calcul profil (cache):', err);
      }

      // Si c'est un compte connecté, marquer comme authentifié
      if (isFromAccount && connectedUser) {
        setIsAuthenticated(true);

        // Convertir l'avatar en base64 si disponible
        let avatarBase64 = '';
        if (cachedUserData.avatarUrl) {
          try {
            avatarBase64 = await convertAvatarToBase64(cachedUserData.avatarUrl);
            logger.log('✅ Avatar converti en base64');
          } catch (error) {
            logger.warn('Impossible de convertir l\'avatar:', error);
          }
        }

        setUserData((prev) => ({
          ...prev,
          gender:              cachedUserData.gender || '',
          height:              cachedUserData.height || '',
          weight:              cachedUserData.weight || '',
          age:                 cachedUserData.age || null,
          deltaChest:          cachedUserData.deltaChest ?? 0,
          deltaWaist:          cachedUserData.deltaWaist ?? 0,
          deltaHips:           cachedUserData.deltaHips  ?? 0,
          manualChest:         cachedUserData.manualChest ?? null,
          manualWaist:         cachedUserData.manualWaist ?? null,
          manualHips:          cachedUserData.manualHips  ?? null,
          computedMeasurements: measurements,
          avatarUrl:           cachedUserData.avatarUrl || '',
          avatarBase64:        avatarBase64,
        }));

        setShowCacheModal(false);
        setCachedUserData(null);
        setIsFromAccount(false);

        // Si on a un avatar ET les données produit, aller directement aux résultats
        if (cachedUserData.avatarUrl && userData.customerDomain && userData.productId) {
          setCurrentSlide(3);
        } else if (!needsSelfie) {
          // VTO inactif → pas besoin de selfie
          setCurrentSlide(3);
        } else {
          // VTO actif mais pas d'avatar → passer par la slide selfie
          setCurrentSlide(2);
        }
      } else {
        // Données du cache local (utilisateur non connecté)
        setUserData((prev) => ({
          ...prev,
          gender:              cachedUserData.gender || '',
          height:              cachedUserData.height || '',
          weight:              cachedUserData.weight || '',
          age:                 cachedUserData.age || null,
          deltaChest:          cachedUserData.deltaChest ?? 0,
          deltaWaist:          cachedUserData.deltaWaist ?? 0,
          deltaHips:           cachedUserData.deltaHips  ?? 0,
          manualChest:         cachedUserData.manualChest ?? null,
          manualWaist:         cachedUserData.manualWaist ?? null,
          manualHips:          cachedUserData.manualHips  ?? null,
          computedMeasurements: measurements,
          selfieBase64:        cachedUserData.selfieBase64 || '',
          avatarBase64:        cachedUserData.avatarBase64 || '',
        }));

        setShowCacheModal(false);
        setCachedUserData(null);
        setIsFromAccount(false);

        // Avatar en cache → sauter le selfie, aller directement aux résultats
        if (cachedUserData.avatarBase64 && userData.customerDomain && userData.productId) {
          setCurrentSlide(3);
        } else if (cachedUserData.selfieBase64 && userData.customerDomain && userData.productId) {
          // Selfie en cache (avant génération avatar) → aller aux résultats
          setCurrentSlide(3);
        } else if (!needsSelfie) {
          // VTO inactif → pas besoin de selfie
          setCurrentSlide(3);
        } else {
          // VTO actif mais pas encore d'avatar ni selfie → slide selfie
          setCurrentSlide(2);
        }
      }
    }
  };

  // Saisir de nouvelles données (reste sur la slide actuelle)
  const handleEnterNewData = async () => {
    // Toujours nettoyer le cache local pour permettre l'enregistrement de nouvelles données
    clearUserCache();

    // Déconnecter l'utilisateur Firebase s'il est connecté
    if (connectedUser) {
      try {
        await signOut();
        setConnectedUser(null);
        setIsAuthenticated(false);
        logger.log('🔓 Utilisateur déconnecté pour saisir de nouvelles données');
      } catch (error) {
        logger.warn('Erreur lors de la déconnexion:', error);
      }
    }

    setShowCacheModal(false);
    setCachedUserData(null);
    setIsFromAccount(false);
    // Reste sur la slide actuelle (slide 0 - page des mesures)
  };

  // Fermer le modal sans action (garde le cache/compte, reste sur la slide actuelle)
  const handleCloseCacheModal = () => {
    setShowCacheModal(false);
    setCachedUserData(null);
    setIsFromAccount(false);
  };

  // Appliquer une correction manuelle de mesure(s) depuis l'écran de résultat.
  // Les deltas en crans existants ne sont jamais réinitialisés ici : seule la
  // dimension effectivement corrigée perd son delta (résolu côté backend).
  const handleProfileCorrected = (newComputedMeasurements, manualValues) => {
    setUserData((prev) => ({
      ...prev,
      computedMeasurements: newComputedMeasurements,
      manualChest: manualValues.chest ?? prev.manualChest ?? null,
      manualWaist: manualValues.waist ?? prev.manualWaist ?? null,
      manualHips: manualValues.hips ?? prev.manualHips ?? null,
    }));
  };

  // Gérer la sauvegarde du compte depuis ResultsSlide
  const handleSaveAccount = async ({ user: authUser, userData: savedData }) => {
    try {
      setIsAuthenticated(true);

      // Mapper gender vers gender_id (1 = homme, 2 = femme)
      // Préparer les données pour createShopper
      // Pour l'instant, on n'envoie que les champs obligatoires
      const shopperData = {
        firebase_id: authUser.uid,
        nickname: 'Mirror User', // Temporaire - à modifier plus tard
        first_name: 'Mirror', // Temporaire - à modifier plus tard
        number_phone: authUser.phoneNumber || '',
        email: `${authUser.uid}@mirror.temp`, // Temporaire - à modifier plus tard
      };

      // Créer le shopper dans mirror-api (PostgREST)
      await createShopper(shopperData);

      // Nettoyer le cache local
      clearUserCache();

      logger.log('✅ Compte créé avec succès dans mirror-api');
    } catch (error) {
      logger.error('Erreur lors de la création du compte:', error);
    }
  };

  // Helpers dérivés de l'abonnement
  // VTO utile seulement si les deux booléens de la paire sont true
  const isVtoFullyActive = !!(subscription?.vto_activation && subscription?.vto_active);

  // Condition bloquante : les deux services sont désactivés par le marchand
  const isBothNotActivated = subscription !== null &&
    !subscription.reco_activation && !subscription.vto_activation;

  // Condition bloquante : les deux services n'ont plus de crédits (mais au moins une activation)
  const isBothNoCredits = subscription !== null &&
    !isBothNotActivated &&
    !subscription.size_reco_active && !subscription.vto_active;

  // Vrai si l'abonnement est chargé ET qu'un écran bloquant va s'afficher
  // → empêche l'ouverture du modal de cache dans ce cas
  const isSubscriptionBlocking = isBothNotActivated || isBothNoCredits;

  // Détermine si la slide selfie doit être incluse dans le flux
  // Le selfie n'est nécessaire que si le VTO est pleinement actif
  const needsSelfie = isVtoFullyActive;

  const renderCurrentSlide = () => {
    switch (currentSlide) {
      case 0:
        return (
          <MeasurementsSlide
            key="measurements"
            onNext={(data) => {
              setUserData((prev) => ({ ...prev, ...data }));
              setCurrentSlide(1);
            }}
            initialData={{
              gender: userData.gender,
              height: userData.height,
              weight: userData.weight,
              age:    userData.age,
            }}
          />
        );
      case 1:
        return (
          <MannequinAdjustSlide
            key="mannequin"
            gender={userData.gender}
            height={userData.height}
            weight={userData.weight}
            age={userData.age}
            existingMeasurements={userData.computedMeasurements}
            initialData={{
              deltaChest: userData.deltaChest,
              deltaWaist: userData.deltaWaist,
              deltaHips:  userData.deltaHips,
            }}
            onNext={async (deltas) => {
              const updated = { ...userData, ...deltas };
              setUserData((prev) => ({ ...prev, ...deltas }));
              try {
                const measurements = await computeProfile({
                  gender:     updated.gender,
                  height:     updated.height,
                  weight:     updated.weight,
                  age:        updated.age,
                  deltaChest: deltas.deltaChest,
                  deltaWaist: deltas.deltaWaist,
                  deltaHips:  deltas.deltaHips,
                });
                setUserData((prev) => ({ ...prev, computedMeasurements: measurements }));
              } catch (err) {
                logger.error('Erreur calcul profil:', err);
              }
              setCurrentSlide(needsSelfie ? 2 : 3);
            }}
            onBack={() => setCurrentSlide(0)}
          />
        );
      case 2:
        return (
          <SelfieSlide
            key="selfie"
            onNext={(data) => {
              setUserData((prev) => ({ ...prev, ...data }));
              setCurrentSlide(3);
            }}
            onBack={() => setCurrentSlide(1)}
            initialSelfie={userData.selfieBase64}
          />
        );
      case 3:
        return (
          <ResultsSlideNew
            key="results"
            userData={userData}
            isAuthenticated={isAuthenticated}
            connectedUser={connectedUser}
            subscription={subscription}
            sessionId={sessionId}
            onSaveAccount={handleSaveAccount}
            onProfileCorrected={handleProfileCorrected}
            onRestart={async () => {
              setSessionId(crypto.randomUUID());
              setCurrentSlide(0);
              setIsAuthenticated(false);
              // Préserver les données du vêtement et de la gateway qui viennent de la page parente
              setUserData((prev) => ({
                gender: '',
                height: '',
                weight: '',
                age: null,
                deltaChest: 0,
                deltaWaist: 0,
                deltaHips: 0,
                manualChest: null,
                manualWaist: null,
                manualHips: null,
                computedMeasurements: null,
                selfieBase64: '',
                clothingItem: prev.clothingItem,
                avatarUrl: '',
                avatarBase64: '',
                customerDomain: prev.customerDomain,
                productId: prev.productId,
                shopperId: '',
              }));

              // Si un utilisateur est connecté, afficher le modal avec ses données
              if (connectedUser) {
                await checkAndShowAccountModal(connectedUser);
              } else if (hasCachedData()) {
                // Sinon, vérifier le cache local
                const cached = getUserDataFromCache();
                if (cached && cached.gender && cached.height && cached.weight) {
                  logger.log('📦 Données en cache détectées après recommencer, affichage du modal');
                  setCachedUserData(cached);
                  setIsFromAccount(false);
                  if (!isSubscriptionBlocking) setShowCacheModal(true);
                }
              }
            }}
          />
        );
      case 4:
        return (
          <PhoneAuthSlide
            key="auth"
            onNext={handlePhoneAuthComplete}
            onSkip={handleSkipAuth}
          />
        );
      default:
        return null;
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Notifier la page parente de la fermeture
    window.parent.postMessage({ type: 'MIRROR_CLOSED' }, '*');
  };

  // Calculer les étapes de progression à afficher
  // Si le selfie est inutile (VTO inactif), on affiche 4 étapes au lieu de 5
  // Slide 4 (PhoneAuth) est un détour optionnel, exclu de la barre de progression
  // Slides: 0=Mesures, 1=Mannequin, 2=Selfie (optionnel), 3=Résultats
  const progressSteps = needsSelfie ? [0, 1, 2, 3] : [0, 1, 3];

  return (
    <>
      {/* Modal de choix (compte connecté ou cache) */}
      {showCacheModal && cachedUserData && !isSubscriptionBlocking && (
        <CacheChoiceModal
          cachedData={cachedUserData}
          isFromAccount={isFromAccount}
          onUseCachedData={handleUseCachedData}
          onEnterNewData={handleEnterNewData}
          onClose={handleCloseCacheModal}
        />
      )}

      {/* Overlay */}
      <div
        className={`mirror-overlay ${isOpen ? 'open' : ''}`}
        onClick={handleClose}
      />

      {/* Popup/Slider */}
      <div className={`mirror-popup ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="popup-header">
          {/* Sélecteur de langue */}
          <LanguageSwitcher />
          {/* Indicateur de progression dans le header */}
          <div className="progress-indicator">
            {progressSteps.map((step) => (
              <div
                key={step}
                className={`progress-dot ${
                  step === currentSlide ? 'active' : ''
                } ${step < currentSlide ? 'completed' : ''}`}
              />
            ))}
          </div>
          <button className="close-button" onClick={handleClose} aria-label={t('buttons.close')}>
            ×
          </button>
        </div>

        {/* Contenu des slides */}
        <div className="slide-container">
          {subscriptionStatus === 'loading' ? (
            <div className="subscription-loading">
              <div className="spinner"></div>
            </div>
          ) : isBothNotActivated ? (
            <SubscriptionErrorScreen reason="notActivated" onClose={handleClose} />
          ) : isBothNoCredits ? (
            <SubscriptionErrorScreen reason="noCredits" onClose={handleClose} />
          ) : (
            renderCurrentSlide()
          )}
        </div>

        {/* Footer */}
        <div className="popup-footer">
          <div className="footer-content">
            <div className="footer-branding">
              <span className="footer-powered">{t('footer.poweredBy')}</span>
              <a href="https://mirror.younzee.com/" target="_blank" rel="noopener noreferrer" className="footer-logo-link">
                <img src={logo} alt="Mirror" className="footer-logo" />
              </a>
            </div>
            <p className="footer-privacy">
              {t('footer.privacyText')}{' '}
              <a href="https://mirror.younzee.com/privacy" target="_blank" rel="noopener noreferrer" className="footer-link">{t('footer.privacyLink')}</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
